import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lightning, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * One-time magic login: /auth/magic?token=xxx
 * Redeems the token, stores the session exactly like a normal login,
 * then hard-redirects to the dashboard (full reload so AuthContext re-checks).
 * Built so AI browser agents (Lindy) can log in by just opening a URL.
 */
export default function MagicLoginPage() {
  const [params] = useSearchParams();
  const [error, setError] = useState(null);
  const fired = useRef(false); // React StrictMode double-mounts — redeem exactly once

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const token = params.get('token');
    if (!token) {
      setError('No token in the link. Ask the admin to generate a fresh login link.');
      return;
    }
    // NOTE: no withCredentials — same-origin (production) gets the httpOnly
    // cookies automatically, and the localStorage tokens below are what the
    // SPA's axios interceptor actually uses for API auth.
    axios.post(`${API_URL}/api/auth/magic`, { token })
      .then(({ data }) => {
        if (data.access_token) localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        window.location.replace('/dashboard');
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Login link failed. Ask the admin for a fresh one.');
      });
  }, [params]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="magic-login-page">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm max-w-md w-full p-8 text-center">
        {!error ? (
          <>
            <Lightning size={40} weight="fill" className="text-[#F39C12] mx-auto mb-4 animate-pulse" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Logging you in…</h1>
            <p className="text-sm text-slate-500">One-time login link — this takes a second.</p>
          </>
        ) : (
          <>
            <Warning size={40} weight="fill" className="text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Link didn&apos;t work</h1>
            <p className="text-sm text-slate-500 mb-6" data-testid="magic-login-error">{error}</p>
            <a href="/login" className="inline-block bg-[#0B5394] hover:bg-[#094180] text-white font-bold text-sm rounded px-5 py-2.5">
              Go to normal login →
            </a>
          </>
        )}
      </div>
    </div>
  );
}
